import React, { Suspense } from 'react';
import { createPortal } from 'react-dom';

// Lazy load the CheckoutDialog component
const CheckoutDialog = React.lazy(() => import('./CheckoutDialog'));

interface LazyCheckoutDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

// Loading skeleton component for CheckoutDialog
const CheckoutDialogSkeleton: React.FC = () => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-[9999]">
      <div className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl rounded-lg w-full h-full max-h-[95vh] flex flex-col animate-pulse">
        {/* Dialog Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <div className="h-6 bg-gray-200 rounded w-20 mb-1"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
            <div className="p-2 w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
          </div>
        </div>

        {/* Dialog Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto px-4">
            {/* Step Indicator Skeleton */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="ml-2 h-4 bg-gray-200 rounded w-12"></div>
                </div>
                <div className="w-12 h-0.5 bg-gray-200"></div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="ml-2 h-4 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                {/* Cart Items Skeleton */}
                <div className="bg-white rounded-lg p-6 shadow-sm border">
                  <div className="h-6 bg-gray-200 rounded w-24 mb-4"></div>
                  <div className="space-y-4">
                    {[1, 2, 3].map((item) => (
                      <div key={item} className="flex items-center gap-4 p-4 border rounded-lg">
                        <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-20"></div>
                        </div>
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {/* User Information Skeleton */}
                <div className="bg-white rounded-lg p-6 shadow-sm border">
                  <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>

                {/* Order Summary Skeleton */}
                <div className="bg-white rounded-lg p-6 shadow-sm border">
                  <div className="h-6 bg-gray-200 rounded w-28 mb-4"></div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </div>
                    <div className="flex justify-between">
                      <div className="h-4 bg-gray-200 rounded w-12"></div>
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </div>
                    <div className="border-t pt-3">
                      <div className="flex justify-between">
                        <div className="h-5 bg-gray-200 rounded w-16"></div>
                        <div className="h-5 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LazyCheckoutDialog: React.FC<LazyCheckoutDialogProps> = (props) => {
  // Only render when dialog is open to prevent unnecessary skeleton display
  if (!props.isOpen) {
    return null;
  }

  return createPortal(
    <Suspense fallback={<CheckoutDialogSkeleton />}>
      <CheckoutDialog {...props} />
    </Suspense>,
    document.body,
  );
};

export default LazyCheckoutDialog;
export { CheckoutDialogSkeleton };
